import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    DoorOpen, KeyRound, ChevronRight, Video, MessageSquare, Send,
    Clock, LogOut, ArrowLeft, ShieldCheck, Building2, RefreshCw, Check,
    Package, Wrench, Car, PackageCheck, Bell, Moon, Info, Users, UserRound,
} from 'lucide-react';
import { tapEntryAPI } from '../lib/api';

const LANGS = [{ id: 'en', label: 'EN' }, { id: 'fr', label: 'FR' }, { id: 'es', label: 'ES' }];
const WARN_MS = 15 * 60 * 1000;
const sessionKey = (loc) => `yot_tap_session:${loc}`;

// Icon per canonical action type (camera is UNIT-level, never a person action).
const ACTION_ICON = {
    message: MessageSquare, deliveries: Package, maintenance: Wrench, parking: Car,
    pickup: PackageCheck, concierge: Bell, im_away: Moon, custom_instruction: Info,
};

function fmtRemaining(ms, t) {
    if (ms <= 0) return '0' + t('tapEntry.minsShort');
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `${h}${t('tapEntry.hoursShort')} ${m}${t('tapEntry.minsShort')}`;
    if (totalMin >= 1) return `${m}${t('tapEntry.minsShort')}`;
    return `<1${t('tapEntry.minsShort')}`;
}

const isVideo = (url) => /\.(mp4|webm|mov|m3u8)(\?|$)/i.test(url || '');

export default function TapEntry() {
    const { locationId } = useParams();
    const { t, i18n } = useTranslation();

    const [screen, setScreen] = useState('loading'); // loading|directory|code|session|ended
    const [directory, setDirectory] = useState({ units: [], show_names: false });
    const [selectedUnit, setSelectedUnit] = useState('');
    const [manualUnit, setManualUnit] = useState('');
    const [code, setCode] = useState('');
    const [session, setSession] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState('message');
    const [messages, setMessages] = useState([]);
    const [waiting, setWaiting] = useState({ waiting: 0, notice: false });
    const [forceThread, setForceThread] = useState(false);
    const [msgText, setMsgText] = useState('');
    const [cameras, setCameras] = useState(null);
    const [now, setNow] = useState(Date.now());
    const threadRef = useRef(null);

    // Person layer (visitor → unit → person → person's actions)
    const [person, setPerson] = useState(null);          // selected person | null (picker)
    const [personActions, setPersonActions] = useState(null); // null=loading, []=none
    const [actionView, setActionView] = useState(null);  // action being viewed | null

    const people = useMemo(() => (session?.people || []), [session]);
    const hasPeople = people.length > 0;
    const threadOpen = screen === 'session' && tab === 'message' && !!session?.message_enabled
        && (forceThread || (hasPeople ? actionView?.type === 'message' : true));

    const localized = useCallback((dict) => {
        if (!dict || typeof dict !== 'object') return '';
        return (dict[i18n.language] || dict.en || Object.values(dict).find((v) => (v || '').trim()) || '').trim();
    }, [i18n.language]);

    const personLabel = useCallback((p, idx) => {
        const nm = (p?.display_name || '').trim();
        if (nm) return nm;
        const role = p?.tenant_role === 'master' ? t('tapEntry.personPrimary') : t('tapEntry.personResident');
        return people.length > 1 && p?.tenant_role !== 'master' ? `${role} ${idx + 1}` : role;
    }, [people.length, t]);

    const actionLabel = useCallback((a) => localized(a?.label) || t(`propertyPeople.at.${a?.type}`, a?.type),
        [localized, t]);

    const setLang = (id) => { i18n.changeLanguage(id); try { localStorage.setItem('yulontap_lang', id); } catch (e) {} };

    useEffect(() => {
        try {
            const stored = localStorage.getItem('yulontap_lang');
            if (stored && LANGS.some((l) => l.id === stored)) i18n.changeLanguage(stored);
        } catch (e) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDirectory = useCallback(async () => {
        try {
            const { data } = await tapEntryAPI.directory(locationId);
            setDirectory({ units: data.units || [], show_names: !!data.show_names });
            setScreen('directory');
        } catch (e) {
            setError(t('tapEntry.notFound'));
            setDirectory({ units: [], show_names: false });
            setScreen('directory');
        }
    }, [locationId, t]);

    const enterSession = useCallback((data) => {
        setSession(data);
        setCameras(null);
        setMessages([]);
        setActionView(null);
        const ppl = data.people || [];
        setPerson(ppl.length === 1 ? ppl[0] : null);
        setPersonActions(null);
        const contactSide = ppl.length > 0 || data.message_enabled;
        setTab(data.camera_enabled && !contactSide ? 'camera' : 'message');
        setScreen('session');
    }, []);

    // Resume on mount
    useEffect(() => {
        (async () => {
            let saved = null;
            try { saved = JSON.parse(localStorage.getItem(sessionKey(locationId)) || 'null'); } catch (e) {}
            if (saved?.token) {
                try {
                    const { data } = await tapEntryAPI.session(saved.token);
                    enterSession({ ...data, token: saved.token });
                    return;
                } catch (e) {
                    try { localStorage.removeItem(sessionKey(locationId)); } catch (e2) {}
                }
            }
            loadDirectory();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId]);

    // Countdown ticker (session only)
    useEffect(() => {
        if (screen !== 'session') return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [screen]);

    const expiresAt = session ? new Date(session.expires_at).getTime() : 0;
    const remaining = expiresAt - now;

    // Expiry -> ended
    useEffect(() => {
        if (screen === 'session' && session && remaining <= 0) {
            setScreen('ended');
            try { localStorage.removeItem(sessionKey(locationId)); } catch (e) {}
        }
    }, [remaining, screen, session, locationId]);

    // Live thread — polled for the WHOLE session, not only while the thread is
    // on screen, so a resident's reply (or "Notify visitor") lights up wherever
    // the visitor is. `seen` is sent only while they are reading it.
    const loadMessages = useCallback(async (seen) => {
        if (!session?.token) return;
        try {
            const { data } = await tapEntryAPI.messages(session.token, !!seen);
            setMessages(data.messages || []);
            setWaiting({ waiting: data.waiting || 0, notice: !!data.notice });
        } catch (e) {
            // Dead-thread recovery: a session that no longer exists ends the
            // screen instead of polling forever behind a silent failure.
            if ([401, 404, 410].includes(e?.response?.status)) setScreen('ended');
        }
    }, [session]);

    useEffect(() => {
        if (screen !== 'session' || !session?.message_enabled) return;
        loadMessages(threadOpen);
        const id = setInterval(() => loadMessages(threadOpen), 5000);
        return () => clearInterval(id);
    }, [screen, threadOpen, session, loadMessages]);

    useEffect(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [messages]);

    // Cameras
    useEffect(() => {
        if (screen !== 'session' || tab !== 'camera' || cameras !== null || !session?.token) return;
        (async () => {
            try {
                const { data } = await tapEntryAPI.cameras(session.token);
                setCameras(data.cameras || []);
            } catch (e) {
                if ([401, 404, 410].includes(e?.response?.status)) setScreen('ended');
                else setCameras([]);
            }
        })();
    }, [screen, tab, cameras, session]);

    // Fetch the selected person's enabled actions
    useEffect(() => {
        if (!person?.person_id || !session?.token) { if (!person) setPersonActions(null); return; }
        setPersonActions(null);
        setActionView(null);
        (async () => {
            try {
                const { data } = await tapEntryAPI.personActions(session.token, person.person_id);
                setPersonActions(data.actions || []);
            } catch (e) {
                if ([401, 404, 410].includes(e?.response?.status)) setScreen('ended');
                else setPersonActions([]);
            }
        })();
    }, [person, session]);

    const pickUnit = (unitNumber) => {
        setSelectedUnit(unitNumber);
        setCode('');
        setError('');
        setScreen('code');
    };

    const submitCode = async () => {
        const unit = (selectedUnit || manualUnit || '').trim();
        if (!unit || code.length !== 4) return;
        setBusy(true); setError('');
        try {
            const { data } = await tapEntryAPI.resolve(locationId, unit, code);
            try { localStorage.setItem(sessionKey(locationId), JSON.stringify({ token: data.token })); } catch (e) {}
            enterSession({ ...data });
        } catch (e) {
            const s = e?.response?.status;
            setError(s === 429 ? t('tapEntry.tooMany') : (e?.response?.data?.detail || t('tapEntry.error')));
            setCode('');
        } finally {
            setBusy(false);
        }
    };

    const sendMessage = async () => {
        const text = msgText.trim();
        if (!text || !session?.token) return;
        setMsgText('');
        try {
            const { data } = await tapEntryAPI.sendMessage(session.token, text, person?.person_id || null);
            setMessages((prev) => [...prev, ...(data.messages || [])]);
        } catch (e) {
            if ([401, 404, 410].includes(e?.response?.status)) setScreen('ended');
            else { setMsgText(text); setError(t('tapEntry.error')); }
        }
    };

    const openAction = (a) => {
        setActionView(a);
        setForceThread(false);
        if (session?.token && a?.id) {
            tapEntryAPI.openAction(session.token, a.id).catch(() => { /* recorded best-effort */ });
        }
    };

    const endSession = async () => {
        if (session?.token) { try { await tapEntryAPI.leave(session.token); } catch (e) {} }
        try { localStorage.removeItem(sessionKey(locationId)); } catch (e) {}
        setSession(null); setSelectedUnit(''); setManualUnit(''); setCode('');
        setForceThread(false);
        loadDirectory();
    };

    const restart = () => {
        setSession(null); setSelectedUnit(''); setManualUnit(''); setCode(''); setError('');
        setForceThread(false);
        loadDirectory();
    };

    // The visitor's own option choices are recorded for the resident's inbox;
    // the visitor's thread shows the conversation itself.
    const visibleMessages = useMemo(() => messages.filter((m) => m.kind !== 'action'), [messages]);

    // Unit-level message thread — reused by the no-people case AND a person's
    // "message" action. Behaviour unchanged; message send/poll is unit-level.
    const renderThread = () => (
        session.message_enabled ? (
            <div data-testid="tap-message-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                    <MessageSquare className="h-3.5 w-3.5" />{t('tapEntry.messageTitle')}
                </div>
                <div ref={threadRef} className="h-72 overflow-y-auto rounded-2xl bg-white/[0.02] border border-white/10 p-3 space-y-2 mb-3">
                    {visibleMessages.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-center text-sm text-slate-500 px-4">
                            {t('tapEntry.messageEmpty')}
                        </div>
                    ) : visibleMessages.map((m) => (
                        <div key={m.id} data-testid={`tap-msg-${m.direction}`}
                            className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm ${m.direction === 'visitor' ? 'ml-auto bg-amber-400 text-slate-950 rounded-br-sm' : 'mr-auto bg-white/[0.06] text-slate-100 rounded-bl-sm border border-white/10'}`}>
                            {m.direction !== 'visitor' && (
                                <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-1">
                                    {m.kind === 'auto_response' ? t('tapEntry.autoReply') : t('tapEntry.residentLabel')}
                                </div>
                            )}
                            <div>{m.text}</div>
                            {m.direction === 'visitor' && (
                                <div className="mt-1 flex items-center justify-end gap-0.5 text-slate-950/45"
                                    title={t('tapEntry.delivered')} aria-label={t('tapEntry.delivered')}
                                    data-testid="tap-msg-delivered">
                                    <Check className="h-3 w-3" strokeWidth={2.5} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input value={msgText} onChange={(e) => setMsgText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder={t('tapEntry.messagePlaceholder')} data-testid="tap-message-input"
                        className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3.5 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/50" />
                    <button onClick={sendMessage} disabled={!msgText.trim()} data-testid="tap-message-send"
                        className="px-4 rounded-xl bg-amber-400 text-slate-950 font-bold disabled:opacity-40">
                        <Send className="h-5 w-5" />
                    </button>
                </div>
            </div>
        ) : (
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-8 text-center text-sm text-slate-400" data-testid="tap-message-disabled">
                {t('tapEntry.messageDisabled')}
            </div>
        )
    );

    // Person layer: picker (multiple) → the selected person's actions → detail.
    const renderPersonLayer = () => {
        if (!person) {
            return (
                <div data-testid="tap-people-picker">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                        <Users className="h-3.5 w-3.5" />{t('tapEntry.whoLookingFor')}
                    </div>
                    <div className="space-y-2">
                        {people.map((p, i) => (
                            <button key={p.person_id} onClick={() => setPerson(p)} data-testid={`tap-person-${p.person_id}`}
                                className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors text-left">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
                                        <UserRound className="h-4 w-4 text-amber-300" />
                                    </div>
                                    <div>
                                        <div className="text-base font-bold text-white">{personLabel(p, i)}</div>
                                        {p.tenant_role === 'master' && (
                                            <div className="text-xs text-slate-400">{t('tapEntry.personPrimary')}</div>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-500" />
                            </button>
                        ))}
                    </div>
                </div>
            );
        }
        const idx = people.findIndex((p) => p.person_id === person.person_id);
        const backOptions = (
            <button onClick={() => setActionView(null)} data-testid="tap-back-options"
                className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4">
                <ArrowLeft className="h-4 w-4" />{t('tapEntry.backToOptions')}
            </button>
        );
        return (
            <div data-testid="tap-person-actions">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">{t('tapEntry.visitingLabel')}</div>
                        <div className="text-lg font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {personLabel(person, idx)}
                        </div>
                    </div>
                    {people.length > 1 && (
                        <button onClick={() => { setPerson(null); setActionView(null); }} data-testid="tap-back-people"
                            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200">
                            <ArrowLeft className="h-4 w-4" />{t('tapEntry.backToPeople')}
                        </button>
                    )}
                </div>

                {actionView ? (
                    actionView.type === 'message' ? (
                        <div>{backOptions}{renderThread()}</div>
                    ) : (
                        <div data-testid="tap-action-detail">
                            {backOptions}
                            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    {(() => { const Ic = ACTION_ICON[actionView.type] || Info; return <Ic className="h-5 w-5 text-amber-300" />; })()}
                                    <div className="text-base font-bold text-white">{actionLabel(actionView)}</div>
                                </div>
                                {localized(actionView.auto_response_text) ? (
                                    <>
                                        <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-1.5">{t('tapEntry.instructionLabel')}</div>
                                        <p className="text-sm text-slate-200 leading-relaxed" data-testid="tap-action-instruction">
                                            {localized(actionView.auto_response_text)}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-sm text-slate-400" data-testid="tap-action-nodetail">{t('tapEntry.actionNoDetail')}</p>
                                )}
                            </div>
                        </div>
                    )
                ) : personActions === null ? (
                    <div className="flex items-center justify-center py-16 text-slate-500" data-testid="tap-actions-loading">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                    </div>
                ) : personActions.length === 0 ? (
                    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-10 text-center text-sm text-slate-400" data-testid="tap-no-actions">
                        {t('tapEntry.noActions')}
                    </div>
                ) : (
                    <>
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">{t('tapEntry.chooseOption')}</div>
                        <div className="space-y-2">
                            {personActions.map((a) => {
                                const Ic = ACTION_ICON[a.type] || Info;
                                return (
                                    <button key={a.id} onClick={() => openAction(a)} data-testid={`tap-action-${a.type}`}
                                        className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors text-left">
                                        <div className="h-9 w-9 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
                                            <Ic className="h-4 w-4 text-amber-300" />
                                        </div>
                                        <div className="flex-1 text-base font-semibold text-white">{actionLabel(a)}</div>
                                        <ChevronRight className="h-5 w-5 text-slate-500" />
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const LangBar = (
        <div className="flex items-center gap-1" data-testid="tap-lang-switcher">
            {LANGS.map((l) => (
                <button key={l.id} onClick={() => setLang(l.id)} data-testid={`tap-lang-${l.id}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide transition-colors ${i18n.language === l.id ? 'bg-amber-400 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}>
                    {l.label}
                </button>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col" data-testid="tap-entry-page"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="pointer-events-none fixed inset-0 opacity-[0.05]"
                style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(251,191,36,0.6), transparent 40%), radial-gradient(circle at 80% 90%, rgba(16,185,129,0.5), transparent 45%)' }} />

            <header className="relative z-10 flex items-center justify-between px-5 py-4 border-b border-white/5">
                <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
                        <DoorOpen className="h-5 w-5 text-amber-300" />
                    </div>
                    <span className="text-sm font-bold tracking-wide uppercase text-slate-200"
                        style={{ fontFamily: 'Outfit, sans-serif' }}>{t('tapEntry.title')}</span>
                </div>
                {LangBar}
            </header>

            <main className="relative z-10 flex-1 w-full max-w-md mx-auto px-5 py-6">
                {screen === 'loading' && (
                    <div className="flex flex-col items-center justify-center py-24 text-slate-500" data-testid="tap-loading">
                        <RefreshCw className="h-6 w-6 animate-spin mb-3" />
                        <span className="text-sm">{t('tapEntry.loading')}</span>
                    </div>
                )}

                {screen === 'directory' && (
                    <div data-testid="tap-directory">
                        <h1 className="text-2xl font-extrabold tracking-tight text-white mb-1"
                            style={{ fontFamily: 'Outfit, sans-serif' }}>{t('tapEntry.welcome')}</h1>
                        <p className="text-sm text-slate-400 mb-6">{t('tapEntry.intro')}</p>

                        {directory.units.length > 0 ? (
                            <>
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                                    <Building2 className="h-3.5 w-3.5" />{t('tapEntry.directoryTitle')}
                                </div>
                                <div className="space-y-2 mb-6">
                                    {directory.units.map((u) => (
                                        <button key={u.room_id} onClick={() => pickUnit(u.unit_number)}
                                            data-testid={`tap-unit-${u.unit_number}`}
                                            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors text-left">
                                            <div>
                                                <div className="text-base font-bold text-white">{u.unit_number}</div>
                                                {directory.show_names && u.label && (
                                                    <div className="text-xs text-slate-400">{u.label}</div>
                                                )}
                                            </div>
                                            <ChevronRight className="h-5 w-5 text-slate-500" />
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-6 mb-6">
                                <p className="text-sm text-slate-400 text-center" data-testid="tap-no-units">
                                    {error || t('tapEntry.noUnits')}
                                </p>
                            </div>
                        )}

                        <div className="rounded-2xl border border-white/10 p-4">
                            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">{t('tapEntry.unitLabel')}</label>
                            <div className="flex gap-2">
                                <input value={manualUnit} onChange={(e) => setManualUnit(e.target.value)}
                                    placeholder={t('tapEntry.enterUnit')} data-testid="tap-manual-unit-input"
                                    className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/50" />
                                <button onClick={() => manualUnit.trim() && pickUnit(manualUnit.trim())}
                                    disabled={!manualUnit.trim()} data-testid="tap-manual-unit-go"
                                    className="px-4 rounded-xl bg-amber-400 text-slate-950 font-bold disabled:opacity-40">
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {screen === 'code' && (
                    <div data-testid="tap-code-screen">
                        <button onClick={restart} data-testid="tap-back-directory"
                            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-6">
                            <ArrowLeft className="h-4 w-4" />{t('tapEntry.changeUnit')}
                        </button>
                        <div className="flex flex-col items-center text-center">
                            <div className="h-14 w-14 rounded-2xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center mb-4">
                                <KeyRound className="h-7 w-7 text-amber-300" />
                            </div>
                            <div className="text-xs uppercase tracking-widest text-slate-500">{t('tapEntry.unitPrefix')}</div>
                            <div className="text-2xl font-extrabold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {selectedUnit || manualUnit}
                            </div>
                            <label className="block text-sm text-slate-400 mb-3">{t('tapEntry.codeLabel')}</label>
                            <input value={code} inputMode="numeric" autoFocus maxLength={4}
                                onChange={(e) => { setError(''); setCode(e.target.value.replace(/\D/g, '').slice(0, 4)); }}
                                onKeyDown={(e) => e.key === 'Enter' && submitCode()}
                                placeholder={t('tapEntry.enterCode')} data-testid="tap-code-input"
                                className="w-48 text-center text-3xl tracking-[0.6em] font-black bg-slate-900 border border-white/10 rounded-2xl px-4 py-4 text-white placeholder:text-slate-700 placeholder:text-base placeholder:tracking-normal placeholder:font-normal focus:outline-none focus:border-amber-400/60" />
                            {error && <p className="text-sm text-rose-400 mt-4" data-testid="tap-code-error">{error}</p>}
                            <button onClick={submitCode} disabled={code.length !== 4 || busy} data-testid="tap-code-submit"
                                className="mt-6 w-full py-3.5 rounded-2xl bg-amber-400 text-slate-950 font-bold uppercase tracking-wide disabled:opacity-40 flex items-center justify-center gap-2">
                                {busy ? <RefreshCw className="h-5 w-5 animate-spin" /> : <><ShieldCheck className="h-5 w-5" />{t('tapEntry.enter')}</>}
                            </button>
                        </div>
                    </div>
                )}

                {screen === 'session' && session && (
                    <div data-testid="tap-session">
                        {/* Session header + subtle countdown */}
                        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 mb-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500">{t('tapEntry.unitPrefix')}</div>
                                    <div className="text-xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {session.unit_number}
                                    </div>
                                </div>
                                <button onClick={endSession} data-testid="tap-end-session"
                                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-300 transition-colors">
                                    <LogOut className="h-4 w-4" />{t('tapEntry.endSession')}
                                </button>
                            </div>
                            <div data-testid="tap-countdown"
                                className={`mt-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 transition-colors ${remaining <= WARN_MS ? 'bg-amber-400/10 text-amber-300 border border-amber-400/30' : 'bg-white/[0.04] text-slate-400 border border-white/10'}`}>
                                <Clock className="h-3.5 w-3.5" />
                                {remaining <= WARN_MS
                                    ? t('tapEntry.endingSoon', { time: fmtRemaining(remaining, t) })
                                    : t('tapEntry.sessionExpiresIn', { time: fmtRemaining(remaining, t) })}
                            </div>
                        </div>

                        {/* A resident replied (or pinged) — visible wherever the
                            visitor is in the session, not only inside the thread. */}
                        {waiting.notice && !threadOpen && session.message_enabled && (
                            <button onClick={() => { setTab('message'); setForceThread(true); }}
                                data-testid="tap-message-waiting"
                                className="w-full mb-4 flex items-center justify-between gap-3 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3.5 text-left transition-colors hover:bg-emerald-500/15">
                                <span className="flex items-center gap-2.5">
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-400/10">
                                        <Bell className="h-4 w-4 text-emerald-300" />
                                    </span>
                                    <span>
                                        <span className="block text-sm font-bold text-white">{t('tapEntry.messageWaiting')}</span>
                                        <span className="block text-xs text-emerald-200/80">{t('tapEntry.openConversation')}</span>
                                    </span>
                                </span>
                                {waiting.waiting > 0 && (
                                    <span className="rounded-full bg-emerald-400 px-2 py-0.5 text-[11px] font-bold text-slate-950"
                                        data-testid="tap-waiting-count">{waiting.waiting}</span>
                                )}
                            </button>
                        )}

                        {/* Tabs — camera (unit-level) alongside the contact/person side */}
                        {session.camera_enabled && (hasPeople || session.message_enabled) && (
                            <div className="flex gap-2 mb-4" data-testid="tap-tabs">
                                <button onClick={() => setTab('message')} data-testid="tap-tab-message"
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === 'message' ? 'bg-amber-400 text-slate-950' : 'bg-white/[0.04] text-slate-300 border border-white/10'}`}>
                                    {hasPeople ? <Users className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                                    {hasPeople ? t('tapEntry.tabContact') : t('tapEntry.tabMessage')}
                                </button>
                                <button onClick={() => setTab('camera')} data-testid="tap-tab-camera"
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === 'camera' ? 'bg-amber-400 text-slate-950' : 'bg-white/[0.04] text-slate-300 border border-white/10'}`}>
                                    <Video className="h-4 w-4" />{t('tapEntry.tabCamera')}
                                </button>
                            </div>
                        )}

                        {/* Contact side: person layer when the unit has people, else the
                            existing unit-level message thread (unchanged fallback). */}
                        {tab === 'message' && (forceThread ? (
                            <div>
                                <button onClick={() => setForceThread(false)} data-testid="tap-thread-back"
                                    className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200">
                                    <ArrowLeft className="h-4 w-4" />{t('tapEntry.backToOptions')}
                                </button>
                                {renderThread()}
                            </div>
                        ) : (hasPeople ? renderPersonLayer() : renderThread()))}

                        {/* Camera */}
                        {tab === 'camera' && (
                            <div data-testid="tap-camera-panel">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                                    <Video className="h-3.5 w-3.5" />{t('tapEntry.cameraHint')}
                                </div>
                                {cameras === null ? (
                                    <div className="flex items-center justify-center py-16 text-slate-500">
                                        <RefreshCw className="h-5 w-5 animate-spin" />
                                    </div>
                                ) : cameras.length === 0 ? (
                                    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-10 text-center text-sm text-slate-400" data-testid="tap-no-cameras">
                                        {t('tapEntry.noCameras')}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {cameras.map((c) => (
                                            <div key={c.id} className="rounded-2xl overflow-hidden bg-black border border-white/10" data-testid={`tap-camera-${c.id}`}>
                                                <div className="px-3 py-2 text-xs font-semibold text-slate-300 bg-white/[0.04] flex items-center gap-2">
                                                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />{c.name}
                                                </div>
                                                <CameraFeed url={c.url} name={c.name}
                                                    aria={`${c.name} — ${t('tapEntry.cameraHint')}`}
                                                    liveLabel={t('tapEntry.live')}
                                                    label={t('tapEntry.cameraUnavailable')} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {screen === 'ended' && (
                    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="tap-ended">
                        <div className="h-16 w-16 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-5">
                            <Clock className="h-8 w-8 text-slate-400" />
                        </div>
                        <h2 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {t('tapEntry.sessionEnded')}
                        </h2>
                        <p className="text-sm text-slate-400 mb-8 max-w-xs">{t('tapEntry.sessionEndedHint')}</p>
                        <button onClick={restart} data-testid="tap-again"
                            className="w-full max-w-xs py-3.5 rounded-2xl bg-amber-400 text-slate-950 font-bold uppercase tracking-wide flex items-center justify-center gap-2">
                            <DoorOpen className="h-5 w-5" />{t('tapEntry.tapAgain')}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}

const canPlayH264 = () => {
    try {
        const v = document.createElement('video');
        return !!(v.canPlayType && v.canPlayType('video/mp4; codecs="avc1.42E01E"'));
    } catch (e) { return true; }
};

function CameraFeed({ url, name, aria, liveLabel, label }) {
    const [failed, setFailed] = useState(false);
    const [needsPlay, setNeedsPlay] = useState(false);
    const [clock, setClock] = useState(() => new Date());
    const ref = useRef(null);
    useEffect(() => {
        const id = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    // iOS inline muted autoplay contract: muted (property + attribute) and
    // playsinline, re-asserted on every attempt. Mirrors CamerasPage.jsx.
    const attemptPlay = useCallback(() => {
        const v = ref.current;
        if (!v) return;
        v.muted = true;
        v.defaultMuted = true;
        v.setAttribute('muted', '');
        const p = v.play();
        if (p && typeof p.then === 'function') p.catch(() => { /* blocked check handles UX */ });
    }, []);

    useEffect(() => {
        const v = ref.current;
        if (!v) return;
        setNeedsPlay(false);
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        attemptPlay();
        const t1 = setTimeout(attemptPlay, 500);
        // Only offer a Play control when autoplay is GENUINELY blocked (still
        // paused once the clip is playable) — never as a permanent overlay.
        const blockedCheck = setTimeout(() => {
            const el = ref.current;
            if (el && el.paused && el.readyState >= 2) setNeedsPlay(true);
        }, 2200);
        return () => { clearTimeout(t1); clearTimeout(blockedCheck); };
    }, [url, attemptPlay]);

    // Belt-and-suspenders loop: restart on end so the feed never shows a
    // stopped/"ended" state if `loop` is ignored.
    const handleEnded = () => {
        const v = ref.current;
        if (!v) return;
        try { v.currentTime = 0; v.play().catch(() => { }); } catch (_) { /* noop */ }
    };

    if (failed || !url) {
        return (
            <div className="aspect-video flex items-center justify-center text-xs text-slate-500 px-4 text-center"
                role="img" aria-label={aria || name}>
                {label}
            </div>
        );
    }
    if (isVideo(url)) {
        // Same supplied clip; prefer WebM only where H.264 is unavailable so the
        // exact supplied MP4 still plays on real devices (incl. iOS Safari).
        const webm = /\.mp4(\?.*)?$/i.test(url) ? url.replace(/\.mp4(\?.*)?$/i, '.webm') : null;
        const src = webm && !canPlayH264() ? webm : url;
        const timeStr = clock.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
        return (
            <div className="relative">
                <video ref={ref} src={src} className="w-full aspect-video object-cover bg-black"
                    autoPlay muted loop playsInline preload="auto"
                    controls={needsPlay}
                    aria-label={aria || name} title={aria || name}
                    onLoadedMetadata={attemptPlay}
                    onLoadedData={attemptPlay}
                    onCanPlay={attemptPlay}
                    onPlaying={() => setNeedsPlay(false)}
                    onEnded={handleEnded}
                    onError={() => { const v = ref.current; if (!v || v.readyState < 2) setFailed(true); }} />
                <div className="pointer-events-none absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur-sm"
                    data-testid="tap-camera-live">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                    <span className="text-[10px] font-semibold tracking-widest text-white/90">{liveLabel}</span>
                </div>
                <div className="pointer-events-none absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/45 backdrop-blur-sm text-[10px] font-mono tabular-nums text-white/75"
                    data-testid="tap-camera-timestamp">
                    {timeStr}
                </div>
            </div>
        );
    }
    return <img src={url} alt={aria || name || ''} className="w-full aspect-video object-cover bg-black" onError={() => setFailed(true)} />;
}
